import React, { useMemo } from 'react';
import { ArrowRight, ListPlus, Share2 } from 'lucide-react';
import { ExtLink } from './Social';
import Waveform, { barsFor } from './Waveform';
import { useAudioPlayer, fmtTime, PlayableTrack } from '@/hooks/useAudioPlayer';
import { useNotice } from '@/hooks/useNotice';
import { ApiTrack } from '@/hooks/useSiteContent';
import { mediaUrl } from '@/lib/api';
import { useHoverPreview } from '@/hooks/useHoverPreview';
import { jukeboxBus } from '@/lib/jukeboxBus';

/** Reference design state for the seeded "Asere" row (only while it has no audio). */
const REF_STATE: Record<string, { position: number; wave: number }> = { asere: { position: 138, wave: 0.58 } };

export default function FeaturedTracks({ tracks, spotifyUrl }: { tracks: ApiTrack[]; spotifyUrl: string }) {
  const { notify } = useNotice();
  const [touched, setTouched] = React.useState(false);
  const visibleTracks = useMemo(() => tracks.slice(0, 10), [tracks]);

  const playable: PlayableTrack[] = useMemo(
    () =>
      visibleTracks.map((t) => ({
        id: t.id,
        title: t.title,
        audioUrl: t.audio_url ? mediaUrl(t.audio_url) : '',
        duration: t.duration || 0,
        initialPosition: !t.audio_url && t.slug && REF_STATE[t.slug] ? REF_STATE[t.slug].position : 0,
      })),
    [visibleTracks],
  );
  const { state, toggle, seek } = useAudioPlayer(playable);
  const blockedOnce = React.useRef(false);
  const { preview, startPreview, stopPreview } = useHoverPreview(() => {
    if (blockedOnce.current) return;
    blockedOnce.current = true;
    notify('Click anywhere on the page once to turn on hover previews.');
  });
  const addToJukebox = (track: ApiTrack) => {
    if (!track.audio_url) {
      notify(`No audio uploaded for “${track.title}” yet.`);
      return;
    }
    const jukebox = jukeboxBus.getState();
    if (!jukebox.ready) {
      notify('Jukebox is still loading. Please try again.');
      return;
    }
    if (!jukebox.enabled) {
      notify('The Jukebox is not available right now.');
      return;
    }
    jukeboxBus.fire({
      type: 'enqueueTrack',
      track: {
        id: track.id,
        title: track.title,
        artist: track.artist,
        audioUrl: track.audio_url,
        coverUrl: track.cover_url,
        externalUrl: track.external_url,
      },
    });
    notify(`Added “${track.title}” to the Jukebox.`);
  };
  const shareTrack = (track: ApiTrack) => {
    const url = track.external_url || `${window.location.origin}${window.location.pathname}#music`;
    const title = `${track.title} — ${track.artist}`;
    if (typeof navigator !== 'undefined' && navigator.share) {
      navigator.share({ title, text: `Listen to ${title}`, url }).catch(() => {});
      return;
    }
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(`Listen to ${title}`)}&url=${encodeURIComponent(url)}`,
      '_blank',
      'noopener,noreferrer',
    );
  };

  return (
    <section
      id="music"
      className="ek-tracks"
      aria-labelledby="tracks-title"
      data-testid="featured-tracks-section"
      style={{ ['--featured-track-count' as string]: visibleTracks.length }}
    >
      <h2 id="tracks-title" className="ek-sec-title ek-tr-title">FEATURED TRACKS</h2>
      <ExtLink url={spotifyUrl} label="Listen on Spotify" className="ek-sec-link ek-tr-link" testId="listen-on-spotify-link">
        LISTEN ON SPOTIFY <ArrowRight size={12} strokeWidth={1.8} />
      </ExtLink>

      <ul className="ek-tr-list">
        {visibleTracks.map((t, i) => {
          const p = playable[i];
          const key = t.slug || t.id;
          const active = state.activeId === t.id;
          const pos = state.positions[t.id] ?? 0;
          const dur = state.durations[t.id] || t.duration || 0;
          const ref = !p.audioUrl && t.slug ? REF_STATE[t.slug] : undefined;
          const designState = !touched && !!ref && i === 0;
          const showPause = active && (state.playing || state.loading || designState);
          const progress = designState ? ref!.wave : dur ? Math.min(1, pos / dur) : 0;
          const pv = preview.id === t.id && preview.status !== 'idle' && dur ? preview : null;
          const pvWin = pv ? { from: pv.from / dur, to: pv.to / dur, at: (pv.status === 'playing' ? pv.pos : pv.from) / dur } : null;
          return (
            <li
              key={t.id}
              className={`ek-tr-row ek-tr-row-${i} ${active ? 'is-active' : ''} ${active && state.playing ? 'is-playing' : ''} ${pv && !active ? 'is-previewing' : ''}`}
              data-testid={`track-row-${key}`}
            >
              {t.cover_url ? (
                <img className="ek-tr-thumb" src={mediaUrl(t.cover_url)} alt={`${t.title} artwork`} draggable={false} />
              ) : (
                <span className="ek-tr-thumb ek-cover-empty" />
              )}
              <div className="ek-tr-text">
                <h3 className="ek-tr-name" title={t.description || t.title}>{t.title}</h3>
                <p className="ek-tr-artist">{t.artist}</p>
              </div>
              <Waveform
                id={key}
                bars={barsFor(t.slug, t.waveform, i)}
                active={active}
                progress={progress}
                preview={pvWin}
                onHoverStart={() => {
                  // previews never interrupt the main player
                  if (!p.audioUrl || state.playing || state.loading) return;
                  startPreview({ id: t.id, url: p.audioUrl, duration: dur, start: t.preview_start });
                }}
                onHoverEnd={() => stopPreview(t.id)}
                onSeek={(f) => {
                  if (!seek(p, f)) notify(p.audioUrl ? 'Press play first to seek.' : `No audio uploaded for “${t.title}” yet.`);
                  else setTouched(true);
                }}
              />
              <span className="ek-tr-time" data-testid={`track-time-${key}`}>
                {pv ? (
                  <>
                    <span className="ek-tr-preview-tag" data-testid={`track-preview-tag-${key}`}>PREVIEW</span>
                    {fmtTime(pv.status === 'playing' ? pv.pos : pv.from)}
                  </>
                ) : (
                  <>
                    {fmtTime(pos)} / {fmtTime(dur)}
                  </>
                )}
              </span>
              <button
                type="button"
                className={`ek-tr-play ${active && state.loading ? 'is-loading' : ''}`}
                aria-label={showPause ? `Pause ${t.title}` : `Play ${t.title}`}
                data-testid={`track-play-${key}`}
                data-state={showPause ? 'pause' : 'play'}
                onClick={() => {
                  setTouched(true);
                  if (!toggle(p)) notify(`No audio uploaded for “${t.title}” yet.`);
                }}
              >
                {showPause ? (
                  <svg viewBox="0 0 12 13" width="12" height="13" aria-hidden="true">
                    <rect x="1" y="0.5" width="3.2" height="12" rx="1.2" fill="currentColor" />
                    <rect x="7.6" y="0.5" width="3.2" height="12" rx="1.2" fill="currentColor" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 12 13" width="12" height="13" aria-hidden="true">
                    <path d="M1.6 1.2c0-.7.8-1.1 1.4-.7l8.2 5.2c.5.3.5 1.1 0 1.5L3 12.4c-.6.4-1.4 0-1.4-.7V1.2z" fill="currentColor" />
                  </svg>
                )}
              </button>
              <button
                type="button"
                className="ek-tr-action"
                aria-label={`Add ${t.title} to Jukebox`}
                title="Add to Jukebox"
                data-testid={`track-add-to-jukebox-${key}`}
                onClick={() => addToJukebox(t)}
              >
                <ListPlus size={15} />
              </button>
              <button
                type="button"
                className="ek-tr-action"
                aria-label={`Share ${t.title}`}
                title="Share"
                data-testid={`track-share-${key}`}
                onClick={() => shareTrack(t)}
              >
                <Share2 size={14} />
              </button>
              <ExtLink
                url={t.external_url}
                label={`More about ${t.title}`}
                className="ek-tr-more"
                testId={`track-more-${key}`}
                onFallback={() => notify(t.description ? `${t.title}: ${t.description}` : `No track page configured for “${t.title}” yet.`)}
              >
                <svg viewBox="0 0 4 14" width="4" height="14" aria-hidden="true">
                  <circle cx="2" cy="1.7" r="1.55" fill="currentColor" />
                  <circle cx="2" cy="7" r="1.55" fill="currentColor" />
                  <circle cx="2" cy="12.3" r="1.55" fill="currentColor" />
                </svg>
              </ExtLink>
            </li>
          );
        })}
      </ul>

      <figure className="ek-crowd" data-testid="culture-card">
        <img src={mediaUrl('/assets/tracks/crowd.jpg')} alt="Concert crowd with raised hands under colourful stage lights" draggable={false} />
        <figcaption>
          <span>MORE THAN MUSIC</span>
          <span>A CULTURE</span>
          <span>A CONNECTION</span>
          <i className="ek-crowd-line" />
        </figcaption>
      </figure>
    </section>
  );
}
